import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { PageHeader } from '../../components/ui/PremiumUI';
import api from '../../services/api';
import { showError, showSuccess } from '../../services/alert';

const statusOptions = {
  todos: 'Todos',
  0: 'Pendente',
  1: 'Pago',
  2: 'Cancelado'
};

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const date = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('pt-BR');
};

const getFileName = (headers) => {
  const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'];
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : 'exportacao-cnab.txt';
};

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const getStatus = (despesa) => Number(despesa?.statusPagamento ?? 0);

const ExportacaoBancaria = () => {
  const [despesas, setDespesas] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [banco, setBanco] = useState('001');
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('0');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  const carregarDespesas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/Despesa');
      const data = Array.isArray(response.data) ? response.data : [];
      setDespesas(data);
      setSelecionadas(data.filter((item) => getStatus(item) === 0).map((item) => item.id));
    } catch (error) {
      console.error('Erro ao carregar despesas para remessa bancária', error);
      showError('Não foi possível carregar as despesas para exportação bancária.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDespesas();
  }, []);

  const despesasFiltradas = useMemo(() => {
    const termo = normalize(busca);
    return despesas.filter((item) => {
      const statusOk = status === 'todos' || String(getStatus(item)) === String(status);
      const texto = normalize([
        item.id,
        item.descricao,
        item.categoriaFinanceira,
        item.safra,
        item.talhao,
        item.notaFiscalNumero
      ].join(' '));
      return statusOk && (!termo || texto.includes(termo));
    });
  }, [busca, despesas, status]);

  const despesasSelecionadas = useMemo(
    () => despesas.filter((item) => selecionadas.includes(item.id)),
    [despesas, selecionadas]
  );

  const totalSelecionado = useMemo(
    () => despesasSelecionadas.reduce((total, item) => total + Number(item.valor || 0), 0),
    [despesasSelecionadas]
  );

  const todasFiltradasSelecionadas = despesasFiltradas.length > 0
    && despesasFiltradas.every((item) => selecionadas.includes(item.id));

  const alternarDespesa = (id) => {
    setSelecionadas((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const alternarTodasFiltradas = () => {
    const idsFiltrados = despesasFiltradas.map((item) => item.id);
    if (todasFiltradasSelecionadas) {
      setSelecionadas((current) => current.filter((id) => !idsFiltrados.includes(id)));
      return;
    }

    setSelecionadas((current) => Array.from(new Set([...current, ...idsFiltrados])));
  };

  const selecionarPendentes = () => {
    setSelecionadas(despesas.filter((item) => getStatus(item) === 0).map((item) => item.id));
    setStatus('0');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (selecionadas.length === 0) {
      showError('Selecione pelo menos uma despesa para gerar o CNAB.');
      return;
    }

    if (!/^\\d{3}$/.test(banco.trim())) {
      showError('Informe o código Febraban do banco com 3 dígitos.');
      return;
    }

    setProcessing(true);
    try {
      const payload = {
        despesaIds: selecionadas,
        bancoDestino: banco.trim()
      };

      const response = await api.post('/financeiro/exportacoes-bancarias', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/plain' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getFileName(response.headers));
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess(`Arquivo CNAB gerado com ${selecionadas.length} despesa(s).`);
    } catch (error) {
      console.error('Erro ao gerar arquivo bancario', error);
      const mensagem = error?.response?.data?.message || 'Não foi possível gerar o arquivo CNAB.';
      showError(mensagem);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Header />
      <main className="cm-page">
        <PageHeader
          eyebrow="Financeiro"
          title="Exportação bancária"
          description="Selecione despesas em aberto e gere arquivo CNAB240 para pagamento."
          actions={(
            <button
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              onClick={() => navigate('/menu')}
              disabled={processing}
              type="button"
            >
              <i className="bi bi-arrow-left" />
              Voltar
            </button>
          )}
        />

          <form onSubmit={handleSubmit}>
            <section className="cm-surface p-3 mb-3">
              <div className="row g-3">
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Banco destino</label>
                  <input
                    type="text"
                    className="form-control"
                    value={banco}
                    maxLength={3}
                    onChange={(event) => setBanco(event.target.value.replace(/\\D/g, '').slice(0, 3))}
                    disabled={processing}
                  />
                  <small className="text-muted">Código Febraban. Ex.: 001.</small>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Status</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    disabled={processing}
                  >
                    {Object.entries(statusOptions).map(([value, label]) => (
                      <option value={value} key={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Buscar</label>
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Descrição, categoria, safra, talhão ou NF"
                    value={busca}
                    onChange={(event) => setBusca(event.target.value)}
                    disabled={processing}
                  />
                </div>

                <div className="col-12 col-md-2 d-grid align-self-md-start">
                  <span className="form-label fw-semibold d-none d-md-block" aria-hidden="true">&nbsp;</span>
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    onClick={carregarDespesas}
                    disabled={loading || processing}
                  >
                    <i className="bi bi-arrow-clockwise me-2" />
                    Atualizar
                  </button>
                </div>
              </div>
            </section>

            <section className="cm-surface p-3 mb-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div>
                  <div className="text-muted small">Selecionado para remessa</div>
                  <div className="fs-5 fw-bold">
                    {selecionadas.length} despesa(s) - {currency(totalSelecionado)}
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={selecionarPendentes}
                    disabled={processing || loading}
                  >
                    Selecionar pendentes
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setSelecionadas([])}
                    disabled={processing || loading || selecionadas.length === 0}
                  >
                    Limpar seleção
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={processing || loading || selecionadas.length === 0}
                  >
                    <i className="bi bi-file-earmark-arrow-down me-2" />
                    {processing ? 'Gerando...' : 'Gerar CNAB'}
                  </button>
                </div>
              </div>
            </section>

            <section className="cm-surface">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 52 }}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={todasFiltradasSelecionadas}
                          onChange={alternarTodasFiltradas}
                          disabled={processing || loading || despesasFiltradas.length === 0}
                          aria-label="Selecionar despesas filtradas"
                        />
                      </th>
                      <th>ID</th>
                      <th>Descrição</th>
                      <th>Vencimento</th>
                      <th>Status</th>
                      <th>Categoria</th>
                      <th>Safra</th>
                      <th>Talhão</th>
                      <th className="text-end">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan="9" className="text-center py-4 text-muted">Carregando despesas...</td>
                      </tr>
                    )}

                    {!loading && despesasFiltradas.length === 0 && (
                      <tr>
                        <td colSpan="9" className="text-center py-4 text-muted">
                          Nenhuma despesa encontrada para os filtros atuais.
                        </td>
                      </tr>
                    )}

                    {!loading && despesasFiltradas.map((despesa) => {
                      const checked = selecionadas.includes(despesa.id);
                      const statusDespesa = getStatus(despesa);
                      return (
                        <tr key={despesa.id} className={checked ? 'table-success' : undefined}>
                          <td>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={checked}
                              onChange={() => alternarDespesa(despesa.id)}
                              disabled={processing}
                              aria-label={`Selecionar despesa ${despesa.id}`}
                            />
                          </td>
                          <td>{despesa.id}</td>
                          <td>{despesa.descricao || '-'}</td>
                          <td>{date(despesa.dataVencimento || despesa.dataPagamento)}</td>
                          <td>
                            <span className={`badge ${statusDespesa === 0 ? 'bg-warning text-dark' : statusDespesa === 1 ? 'bg-success' : 'bg-secondary'}`}>
                              {despesa.statusPagamentoDescricao || statusOptions[statusDespesa] || '-'}
                            </span>
                          </td>
                          <td>{despesa.categoriaFinanceira || '-'}</td>
                          <td>{despesa.safra || '-'}</td>
                          <td>{despesa.talhao || '-'}</td>
                          <td className="text-end fw-semibold">{currency(despesa.valor)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </form>
      </main>
    </>
  );
};

export default ExportacaoBancaria;
